class ObserveHeight extends HTMLElement {
  constructor() {
    super();
    this.varName = this.getAttribute('data-var-name') || '--element-height';
    this.updateElementHeight = this.updateElementHeight.bind(this);
  }

  connectedCallback() {
    this.updateElementHeight();
    window.addEventListener('resize', this.updateElementHeight);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.updateElementHeight);
  }

  updateElementHeight() {
    const height = this.offsetHeight;
    this.style.setProperty(this.varName, `${height}px`);
    this.closest('.shopify-section').style.setProperty(this.varName, `${height}px`);
  }
}

customElements.define('observe-height', ObserveHeight);

class ScheduledSection extends HTMLElement {
  connectedCallback() {
    this.isDesignMode = window.Shopify && Shopify.designMode;
    this.sectionId = this.getAttribute('data-section-id');

    if (this.isDesignMode) {
      document.addEventListener('shopify:section:select', this.onSectionSelect.bind(this));
      document.addEventListener('shopify:section:deselect', this.onSectionDeselect.bind(this));
    }

    this.handleSection();
  }

  onSectionSelect(event) {
    this.handleSection();
  }

  onSectionDeselect(event) {
    this.handleSection();
  }

  parseLocalDate(dateStr) {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed
  }

  handleSection() {
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);

    const isSelectedSection = this.isDesignMode && this.sectionId === window.lastSelectedSectionId;

    if (isSelectedSection) {
      this.classList.remove('hidden');
      return;
    }

    const startDateStr = this.getAttribute('data-start-date');
    const endDateStr = this.getAttribute('data-end-date');

    if (!startDateStr || !endDateStr) {
      this.classList.add('hidden');
      return;
    }

    const startDate = this.parseLocalDate(startDateStr);
    const endDate = this.parseLocalDate(endDateStr);

    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);

    const isWithinRange = currentDate >= startDate && currentDate <= endDate;

    this.classList.toggle('hidden', !isWithinRange);
  }
}

customElements.define('scheduled-section', ScheduledSection);

/**
 * Intercept fetch requests to /cart/add.js
 */
(function () {
  const originalFetch = window.fetch;

  window.fetch = function (...args) {
    const [resource] = args;

    if (
      resource &&
      (resource.includes('/cart/add.js') || resource.includes('/cart/update')) &&
      !resource.includes('no_reload=true')
    ) {
      return originalFetch.apply(this, args).then((response) => {
        if (response.ok) {
          Alpine.store('cart').getCartAndDrawerUpdated();
        }
        return response;
      });
    }

    return originalFetch.apply(this, args);
  };
})();

function handleAccessibilityNav(e) {
  const focusKeys = ['Tab', 'ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'];

  if (!focusKeys.includes(e.key)) return;
  window.removeEventListener('keydown', handleAccessibilityNav);
  document.documentElement.classList.add('focus-is-visible');
}

window.addEventListener('keydown', handleAccessibilityNav);

/**
 * Clipboard helper (trigger + target).
 */
(function () {
  function playCopyAnimation(trigger) {
    trigger.animate(
      [
        { transform: 'scale(1)', filter: 'brightness(1)' },
        { transform: 'scale(0.9)', filter: 'brightness(1.35)' },
        { transform: 'scale(1.06)', filter: 'brightness(1.15)' },
        { transform: 'scale(1)', filter: 'brightness(1)' },
      ],
      { duration: 320, easing: 'ease-out' },
    );
  }

  document.addEventListener('click', async (event) => {
    const trigger = event.target.closest('[data-copy-trigger][data-copy-target]');
    if (!trigger) return;

    const target = document.querySelector(trigger.dataset.copyTarget);
    if (!target) return;

    const text = 'value' in target ? target.value : target.textContent?.trim();
    await navigator.clipboard.writeText(text);
    playCopyAnimation(target);
  });
})();

/**
 * Add aid param to hrefs if it's not already present
 */
(function () {
  const protocolRegex = /^[a-zA-Z][a-zA-Z\d+\-.]*:/;
  const internalDomainKeywords = ['fasterway', 'faster-way'];
  let lastAid = null;

  const isRelativeHref = (href) => !protocolRegex.test(href) && !href.startsWith('//');

  const shouldSyncHref = (href, parsedUrl) => {
    const lowerHref = href.toLowerCase();
    const lowerHostname = parsedUrl.hostname.toLowerCase();
    if (['#', 'javascript:', 'mailto:', 'tel:'].some((prefix) => lowerHref.startsWith(prefix))) {
      return false;
    }

    return (
      isRelativeHref(href) ||
      internalDomainKeywords.some((keyword) => lowerHref.includes(keyword) || lowerHostname.includes(keyword))
    );
  };

  const syncAidLinks = (force = false) => {
    const aid = new URLSearchParams(window.location.search).get('aid');
    if (aid === lastAid && !force) return;
    lastAid = aid;
    if (!aid) return;

    document.querySelectorAll('a[href]').forEach((anchor) => {
      const href = anchor.getAttribute('href');
      if (!href) return;

      let parsedUrl;
      try {
        parsedUrl = new URL(href, window.location.href);
      } catch {
        return;
      }

      if (!shouldSyncHref(href, parsedUrl) || parsedUrl.searchParams.get('aid') === aid) return;

      parsedUrl.searchParams.set('aid', aid);
      const nextHref =
        isRelativeHref(href) && parsedUrl.origin === window.location.origin
          ? `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`
          : parsedUrl.toString();

      anchor.setAttribute('href', nextHref);
    });
  };

  function assignCorrectProgramUrl() {
    const currentCoach = Alpine?.store('fasterway')?.currentCoach || window.currentCoachData || null;
    const currentCreator = Alpine?.store('fasterway')?.currentCreator || window.currentCreatorData || null;

    const selectedCoach = [currentCreator, currentCoach].find((creator) => creator && creator.isCoach);

    if (!selectedCoach) return false;

    document
      .querySelectorAll(`a[href*="${fwcHostname}"], a[${fwcOriginalAttr}*="${fwcHostname}"]`)
      .forEach((anchor) => {
        const href = anchor.getAttribute(fwcOriginalAttr) || anchor.getAttribute('href');
        const parsedUrl = new URL(href, window.location.href);
        if (parsedUrl.hostname !== fwcHostname || parsedUrl.pathname !== '/') return;

        anchor.setAttribute('href', selectedCoach.system.url);
        if (!anchor.hasAttribute(fwcOriginalAttr)) anchor.setAttribute(fwcOriginalAttr, href);
      });

    return true;
  }

  function restoreAllOriginalProgramUrls() {
    document.querySelectorAll(`a[${fwcOriginalAttr}*="${fwcHostname}"]`).forEach((anchor) => {
      anchor.setAttribute('href', anchor.getAttribute(fwcOriginalAttr));
    });
  }

  const fwcHostname = 'fasterwaycoach.com';
  const fwcOriginalAttr = 'data-fwc-original-href';

  assignCorrectProgramUrl();
  setTimeout(syncAidLinks, 500);
  document.addEventListener('fasterway:coach-changed', () => {
    if (assignCorrectProgramUrl() === false) restoreAllOriginalProgramUrls();
    setTimeout(syncAidLinks, 500);
  });
  document.addEventListener('fasterway:sync-aid-links', () => {
    syncAidLinks(true);
  });
})();

(function () {
  function replaceUrl(oldUrl, newUrl) {
    document.querySelectorAll(`a[href*="${oldUrl}"], a[data-original-href*="${oldUrl}"]`).forEach((anchor) => {
      const href = anchor.getAttribute('data-original-href') || anchor.getAttribute('href');
      const newHref = href.replace(oldUrl, newUrl);
      anchor.setAttribute('href', newHref);
      if (!anchor.hasAttribute('data-original-href')) anchor.setAttribute('data-original-href', href);
    });
  }

  const template = window.theme.template;
  if (template.name === 'creator' && template.suffix === 'corporate') {
    replaceUrl('fasterwaycoach.com/mens-program', 'fasterwaytofatloss.com/men/home');
    replaceUrl('fasterwaycoach.com/vip-membership', 'fasterwaytofatloss.com/vip-membership');
  }

  function urlsForReplace() {
    const isSpanish = window.Shopify.locale == 'es';
    const currentCoach = Alpine?.store('fasterway')?.currentCoach || window.currentCoachData || null;
    const isCorporate = currentCoach?.isCorporateCoach;

    if (isCorporate && isSpanish) {
      replaceUrl('/es/products/ab-lab', '/es/products/ab-lab');
    }

    if (isCorporate && !isSpanish) {
      replaceUrl('/products/ab-lab', '/products/ab-lab');
    }

    if (!isCorporate && isSpanish) {
      replaceUrl('/es/products/ab-lab', 'https://www.fasterwaycoach.com/ab-lab-sp');
    }

    if (!isCorporate && !isSpanish) {
      replaceUrl('/products/ab-lab', 'https://www.fasterwaycoach.com/ab-lab');
    }

    if (isSpanish) {
      replaceUrl('https://www.fasterwaycoach.com/vip-membership', 'https://www.fasterwaycoach.com/sp/membresia-vip');
      replaceUrl('https://www.fasterwaytofatloss.com/certification', 'https://www.fasterwaycoach.com/sp/certificacion');
      replaceUrl(
        'https://www.fasterwaytofatloss.com/manage-membership',
        'https://account.fasterwaytofatloss.com/manage-membership-sp',
      );
    }
  }

  urlsForReplace();
  document.addEventListener('fasterway:coach-changed', (e) => {
    urlsForReplace();
  });
})();
